import { useEffect, useState, useRef } from "react";
import { Layout } from "../../components/layout/Layout";
import { api, UPLOADS_BASE, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../../components/ui/command";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Plus, Pencil, Trash2, Package, PackageX, Search, ArrowUpDown, Warehouse, Truck, ClipboardCheck, CheckCircle2, Ban, Settings2, AlertTriangle, Check, ChevronsUpDown, BellRing, ArrowLeftRight, Image as ImageIcon, X, Repeat2, Tag, CornerDownRight, LayoutGrid, Database } from "lucide-react";
import { DatabaseTab } from "./DatabaseTab";
import { CategoryIcon, resolveCategoryForIcon, getCategoryBreadcrumb } from "../../lib/categoryIcons";
import { toast } from "sonner";
import { TransformationTab } from "./TransformationTab";
import { NumericKeypad } from "../../components/NumericKeypad";
import { TagPicker } from "../../components/TagPicker";
import { DenseTableView } from "./views/DenseTableView";
import { CompactListView } from "./views/CompactListView";
import { GroupedCategoryView } from "./views/GroupedCategoryView";
import { PriorityStockView } from "./views/PriorityStockView";
import { STOCK_VIEW_MODES, STOCK_VIEW_STORAGE_KEY } from "../../lib/stockDisplay";

const STOCK_TYPES = [
  { value: "principal", label: "Stock principal" },
  { value: "autre_fournisseur", label: "Autre fournisseur" },
];

const stockTypeLabel = (v) => STOCK_TYPES.find((s) => s.value === v)?.label || "Stock principal";

const SORT_OPTIONS = [
  { value: "date_desc", label: "Date (récent → ancien)" },
  { value: "date_asc", label: "Date (ancien → récent)" },
  { value: "qte_desc", label: "Quantité (haut → bas)" },
  { value: "qte_asc", label: "Quantité (bas → haut)" },
  { value: "nom_asc", label: "Nom (A → Z)" },
  { value: "nom_desc", label: "Nom (Z → A)" },
  { value: "prix_desc", label: "Prix (haut → bas)" },
  { value: "prix_asc", label: "Prix (bas → haut)" },
];

// Constructeur de composants d'une formule (voir Administrateurs > Boutiques
// > Modèle de l'onglet Vente, modèle "menus_formules") : chaque composant
// est un ARTICLE RÉEL du stock (jamais une formule elle-même), avec une
// photo de son prix TTC et de sa TVA au moment de l'ajout — sert uniquement
// à répartir proportionnellement le prix fixe de la formule entre ses
// composants (voir CaissePage.jsx), sans dépendre du prix réel de l'article
// au moment de la vente.
function FormuleComposantsEditor({ articles, composants, onChange }) {
  const [selectedArticleId, setSelectedArticleId] = useState("");
  const [quantite, setQuantite] = useState(1);

  const ajouterComposant = () => {
    const article = articles.find((a) => a.id === selectedArticleId);
    if (!article) return;
    onChange([...composants, {
      article_id: article.id, nom: article.nom, quantite: Number(quantite) || 1,
      tva_percent: article.tva_percent ?? 20, prix_ttc_reference: article.prix_ttc ?? article.prix ?? 0,
    }]);
    setSelectedArticleId("");
    setQuantite(1);
  };

  const retirerComposant = (index) => onChange(composants.filter((_, i) => i !== index));
  const totalReference = composants.reduce((sum, c) => sum + c.prix_ttc_reference * c.quantite, 0);
  const articlesDisponibles = articles.filter((a) => a.type !== "formule");

  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <p className="text-xs font-medium text-muted-foreground">Composants de la formule</p>
      {composants.map((c, i) => (
        <div key={i} className="flex items-center justify-between text-sm bg-secondary/50 rounded px-2 py-1.5" data-testid={`formule-composant-${i}`}>
          <span className="truncate">{c.quantite > 1 ? `${c.quantite}x ` : ""}{c.nom} <span className="text-muted-foreground">({c.tva_percent}%)</span></span>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-muted-foreground">{(c.prix_ttc_reference * c.quantite).toFixed(2)} €</span>
            <button type="button" onClick={() => retirerComposant(i)} data-testid={`formule-composant-remove-${i}`}><X size={14} /></button>
          </div>
        </div>
      ))}
      {composants.length === 0 && <p className="text-xs text-muted-foreground">Aucun composant ajouté.</p>}
      <div className="flex gap-2">
        <Select value={selectedArticleId} onValueChange={setSelectedArticleId}>
          <SelectTrigger className="flex-1" data-testid="formule-composant-select"><SelectValue placeholder="Ajouter un article..." /></SelectTrigger>
          <SelectContent>
            {articlesDisponibles.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom} — {(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="number" min="1" className="w-16" value={quantite} onChange={(e) => setQuantite(e.target.value)} data-testid="formule-composant-quantite" />
        <Button type="button" size="sm" onClick={ajouterComposant} disabled={!selectedArticleId} data-testid="formule-composant-add"><Plus size={14} /></Button>
      </div>
      {composants.length > 0 && (
        <p className="text-xs text-muted-foreground pt-1 border-t border-border">
          Prix normal si achetés séparément : <span className="font-medium">{totalReference.toFixed(2)} €</span>
        </p>
      )}
    </div>
  );
}

export default function StockPage() {
  const { user } = useAuth();
  const [isTestMode, setIsTestMode] = useState(false);

  // Disposition d'affichage choisie par l'utilisateur (grille, tableau,
  // liste, groupé, priorité) — préférence purement personnelle, mémorisée
  // dans ce navigateur uniquement (pas de synchronisation entre appareils).
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(STOCK_VIEW_STORAGE_KEY) || "grille");
  const changerViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem(STOCK_VIEW_STORAGE_KEY, mode);
  };

  // "Fixer prix" (demande explicite) : parcours rapide, article par article,
  // de tous les articles dont le prix de vente a été fixé automatiquement —
  // avec un pavé numérique tactile pour une saisie facile sur téléphone.
  // La liste est figée au moment de l'ouverture (pas recalculée en direct à
  // chaque enregistrement) pour que le parcours reste stable et prévisible
  // même si un rechargement de fond change l'état des articles pendant la
  // saisie.
  const [fixerPrixOpen, setFixerPrixOpen] = useState(false);
  const [fixerPrixListe, setFixerPrixListe] = useState([]);
  const [fixerPrixIndex, setFixerPrixIndex] = useState(0);
  const [fixerPrixValeur, setFixerPrixValeur] = useState("");
  const [fixerPrixSaving, setFixerPrixSaving] = useState(false);
  const articleAFixer = fixerPrixListe[fixerPrixIndex] || null;

  const ouvrirFixerPrix = () => {
    const liste = articles.filter((a) => a.prix_vente_auto && Number(a.quantite) !== 0);
    if (liste.length === 0) return;
    setFixerPrixListe(liste);
    setFixerPrixIndex(0);
    setFixerPrixValeur("");
    setFixerPrixOpen(true);
  };

  const keypadAppuyer = (touche) => {
    if (touche === "back") {
      setFixerPrixValeur((v) => v.slice(0, -1));
    } else if (touche === ".") {
      setFixerPrixValeur((v) => (v.includes(".") ? v : (v || "0") + "."));
    } else {
      setFixerPrixValeur((v) => (v === "0" ? touche : v + touche));
    }
  };

  const validerPrixFixe = async () => {
    const prix = parseFloat(fixerPrixValeur.replace(",", "."));
    if (!prix || prix <= 0) {
      toast.error("Indiquez un prix valide");
      return;
    }
    setFixerPrixSaving(true);
    try {
      await api.patch(`/stock/${articleAFixer.id}`, { prix_ttc: prix });
      toast.success(`Prix fixé pour ${articleAFixer.nom}`);
      if (fixerPrixIndex + 1 < fixerPrixListe.length) {
        setFixerPrixIndex((i) => i + 1);
        setFixerPrixValeur("");
      } else {
        toast.success("Tous les prix ont été fixés !");
        setFixerPrixOpen(false);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setFixerPrixSaving(false);
    }
  };

  // Clavier physique utilisable pendant la saisie du prix (mêmes touches que
  // le pavé tactile : chiffres, virgule/point pour la décimale, retour
  // arrière, Entrée pour valider) — actif uniquement pendant que ce dialogue
  // précis est ouvert.
  useEffect(() => {
    if (!fixerPrixOpen) return;
    const onKeyDown = (e) => {
      if (fixerPrixSaving) return;
      if (/^[0-9]$/.test(e.key)) { keypadAppuyer(e.key); return; }
      if (e.key === "," || e.key === ".") { keypadAppuyer("."); return; }
      if (e.key === "Backspace") { keypadAppuyer("back"); return; }
      if (e.key === "Enter") { validerPrixFixe(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixerPrixOpen, fixerPrixSaving, fixerPrixValeur, articleAFixer]);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);

  // Catégories triées pour l'affichage hiérarchique du sélecteur (fiche
  // article) : chaque catégorie principale suivie immédiatement de ses
  // propres sous-catégories, indentées.
  const categoriesOrdonnees = categories
    .filter((c) => !c.parent_id)
    .flatMap((principale) => [
      { ...principale, isSub: false },
      ...categories.filter((c) => c.parent_id === principale.id).map((sub) => ({ ...sub, isSub: true })),
    ]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [stockFilter, setStockFilter] = useState("tous");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  // La TVA se configure ici, sur l'article : on saisit le prix TTC et le taux de
  // TVA (20% par défaut), le prix H.T est recalculé automatiquement. La caisse ne
  // permet plus de modifier la TVA — elle reprend celle définie sur chaque article.
  const [form, setForm] = useState({ nom: "", quantite: 0, categorie: "", tva_percent: 20, prix_ttc: 0, prix_achat: 0, code: "", stock_type: "principal", seuil_alerte: "", perissable: false, eco_participation_ht: "", vendu_au_poids: false, type: "article", composants: [], duree_minutes: null, stock_infini: false, tags: [] });
  const [photoFile, setPhotoFile] = useState(null);
  // Banque d'images mutualisée (voir routers/stock_routes.py, /stock/image-bank) :
  // remplace le simple champ de téléversement — permet de réutiliser une image
  // déjà envoyée pour un autre article, sans devoir la retéléverser.
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null); // aperçu local avant enregistrement
  const [pendingBankImageId, setPendingBankImageId] = useState(null); // image existante choisie dans la banque
  const [imageBankOpen, setImageBankOpen] = useState(false);
  const [imageBank, setImageBank] = useState([]);
  const [imageBankSearch, setImageBankSearch] = useState("");
  const [imageBankLoading, setImageBankLoading] = useState(false);
  const bankFileInputRef = useRef(null);

  const loadImageBank = async () => {
    setImageBankLoading(true);
    try {
      const { data } = await api.get("/stock/image-bank");
      setImageBank(data);
    } catch {
      setImageBank([]);
    } finally {
      setImageBankLoading(false);
    }
  };

  const openImageBank = () => {
    setImageBankOpen(true);
    setImageBankSearch("");
    loadImageBank();
  };

  const selectBankImage = (img) => {
    setPendingBankImageId(img.id);
    setPhotoFile(null);
    setPhotoPreviewUrl(`${UPLOADS_BASE}${img.url}`);
    setImageBankOpen(false);
  };

  const handleNewBankFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPendingBankImageId(null);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    setImageBankOpen(false);
    e.target.value = "";
  };

  const clearPendingPhoto = () => {
    setPhotoFile(null);
    setPendingBankImageId(null);
    setPhotoPreviewUrl(null);
  };

  // --- Vérification de stock (inventaire tournant) ---
  const [verifArticles, setVerifArticles] = useState([]);
  const [verifDelai, setVerifDelai] = useState(30);
  const [verifDraft, setVerifDraft] = useState(30);
  const [verifSaving, setVerifSaving] = useState(false);

  // --- Produits hors stock (quantité tombée à zéro) ---
  const [hsArticles, setHsArticles] = useState([]);

  // --- Stock bas (seuil de réapprovisionnement configuré et atteint) ---
  const [lowStockArticles, setLowStockArticles] = useState([]);

  // --- Transfert de stock inter-boutique ---
  const [shops, setShops] = useState([]);
  // Config Stock (voir Administrateurs > Config Stock, ConfigStockTab dans
  // AdminPage.jsx) : simplifie ce formulaire selon l'activité de la
  // boutique — types d'article proposés et champs optionnels visibles.
  // Valeurs par défaut si la boutique n'a pas encore explicitement de
  // config (boutiques créées avant l'introduction de ce réglage).
  const DEFAULT_STOCK_CONFIG = {
    types_actifs: { article: true, formule: true, service: true },
    champs_visibles: { perissable: true, vendu_au_poids: true, eco_participation: true, emplacement: true },
    stock_infini_active: false,
  };
  const currentShop = shops.find((s) => s.id === user?.effective_shop_id);
  const stockConfig = currentShop?.stock_config || DEFAULT_STOCK_CONFIG;
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferArticle, setTransferArticle] = useState(null);
  const [transferDestId, setTransferDestId] = useState("");
  const [transferQty, setTransferQty] = useState("");
  const [transferring, setTransferring] = useState(false);
  const canTransfer = !!user?.is_multi_shop_user && hasPerm(user, "stock", "edit");

  // Un utilisateur peut ouvrir la fiche (bouton Modifier) dès qu'il a au moins
  // un droit de modification sur le stock — auparavant seul "edit" (infos
  // générales) donnait accès au bouton, ce qui empêchait par exemple un
  // utilisateur n'ayant que "edit_quantity" d'accéder au formulaire.
  const canEditAnyStockField = ["edit", "edit_prix_achat", "edit_prix_vente", "edit_quantity", "edit_stock_type"]
    .some((action) => hasPerm(user, "stock", action));

  const prixHtPreview = (() => {
    const ttc = Number(form.prix_ttc) || 0;
    const tva = Number(form.tva_percent) || 0;
    return ttc / (1 + tva / 100);
  })();

  const load = async () => {
    const { data } = await api.get("/stock");
    setArticles(data);
  };
  const loadCategories = async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch (e) {
      // silencieux : boutique de travail pas encore sélectionnée
    }
  };
  useEffect(() => { load(); loadCategories(); loadVerification(); loadHorsStock(); loadLowStock(); loadShops(); }, []);

  // Mode Test/École (voir TestModeBanner.jsx) : sert uniquement à afficher en
  // plus le vrai stock de production à côté de la quantité de test, pour
  // qu'on ne confonde jamais les deux pendant l'entraînement (le bandeau
  // d'avertissement en haut de page suffit déjà à signaler le mode actif,
  // ceci est un complément localisé directement sur chaque article).
  useEffect(() => {
    if (!hasPerm(user, "caisse", "access")) return;
    api.get("/caisse/mode").then(({ data }) => setIsTestMode(data.mode === "test")).catch(() => {});
  }, [user]);

  const loadVerification = async () => {
    try {
      const { data } = await api.get("/stock/verification");
      setVerifArticles(data.articles || []);
      setVerifDelai(data.delai_jours ?? 30);
      setVerifDraft(data.delai_jours ?? 30);
    } catch (e) {
      // silencieux : boutique de travail pas encore sélectionnée
    }
  };

  const loadHorsStock = async () => {
    try {
      const { data } = await api.get("/stock/hors-stock");
      setHsArticles(data || []);
    } catch (e) {
      // silencieux : pas de permission ou boutique pas encore sélectionnée
    }
  };

  const loadLowStock = async () => {
    try {
      const { data } = await api.get("/stock/low-stock");
      setLowStockArticles(data || []);
    } catch (e) {
      // silencieux : pas de permission ou boutique pas encore sélectionnée
    }
  };

  const loadShops = async () => {
    try {
      const { data } = await api.get("/shops");
      setShops(data || []);
    } catch (e) {
      // silencieux
    }
  };

  const openTransfer = (a) => {
    setTransferArticle(a);
    setTransferDestId("");
    setTransferQty("");
    setTransferOpen(true);
  };

  const submitTransfer = async () => {
    if (!transferDestId) {
      toast.error("Choisissez une boutique de destination");
      return;
    }
    const qty = Number(transferQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Saisissez une quantité valide");
      return;
    }
    setTransferring(true);
    try {
      await api.post(`/stock/${transferArticle.id}/transfer`, { destination_shop_id: transferDestId, quantite: qty });
      toast.success("Transfert effectué");
      setTransferOpen(false);
      load();
      loadLowStock();
      loadHorsStock();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTransferring(false);
    }
  };

  const saveVerifSettings = async () => {
    setVerifSaving(true);
    try {
      await api.put("/stock/verification/settings", { delai_jours: Number(verifDraft) || 30 });
      toast.success("Délai de vérification mis à jour");
      loadVerification();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setVerifSaving(false);
    }
  };

  const verifConfirmOk = async (id) => {
    try {
      await api.post(`/stock/${id}/verification/confirm`);
      toast.success("Stock confirmé bon");
      loadVerification();
      loadHorsStock();
      loadLowStock();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const verifMarkRupture = async (id) => {
    if (!window.confirm("Marquer cet article en rupture permanente ? Il ne sera plus proposé pour la vérification de stock.")) return;
    try {
      await api.post(`/stock/${id}/verification/rupture`);
      toast.success("Article marqué en rupture permanente");
      loadVerification();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const verifDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement cet article du stock ?")) return;
    try {
      await api.delete(`/stock/${id}`);
      toast.success("Article supprimé");
      load();
      loadVerification();
      loadHorsStock();
      loadLowStock();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const formatLastActivity = (iso) => {
    if (!iso) return "Jamais";
    try {
      return new Date(iso).toLocaleDateString("fr-FR");
    } catch {
      return "—";
    }
  };

  const searchActive = search.trim().length > 0;

  const filtered = articles
    .filter((a) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (a.nom || "").toLowerCase().includes(q) || (a.categorie || "").toLowerCase().includes(q) || (a.code || "").toLowerCase().includes(q) || (a.tags || []).some((t) => t.toLowerCase().includes(q));
    })
    .filter((a) => stockFilter === "tous" || (a.stock_type || "principal") === stockFilter)
    // Un article dont la quantité est tombée à zéro quitte la liste principale
    // du Stock et n'est visible ici que s'il correspond à une recherche
    // explicite (il apparaît alors avec la mention "Produits hors stock").
    .filter((a) => searchActive || Number(a.quantite ?? 0) !== 0)
    .sort((a, b) => {
      switch (sortBy) {
        case "date_asc":
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "qte_asc":
          return (a.quantite || 0) - (b.quantite || 0);
        case "qte_desc":
          return (b.quantite || 0) - (a.quantite || 0);
        case "nom_asc":
          return (a.nom || "").localeCompare(b.nom || "");
        case "nom_desc":
          return (b.nom || "").localeCompare(a.nom || "");
        case "prix_asc":
          return (a.prix_ttc ?? a.prix ?? 0) - (b.prix_ttc ?? b.prix ?? 0);
        case "prix_desc":
          return (b.prix_ttc ?? b.prix ?? 0) - (a.prix_ttc ?? a.prix ?? 0);
        case "date_desc":
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

  const openCreate = () => {
    setEditing(null);
    setForm({ nom: "", quantite: 0, categorie: "", tva_percent: 20, prix_ttc: 0, prix_achat: 0, code: "", stock_type: "principal", seuil_alerte: "", perissable: false, eco_participation_ht: "", vendu_au_poids: false, type: "article", composants: [], duree_minutes: null, stock_infini: false, tags: [] });
    clearPendingPhoto();
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      nom: a.nom,
      quantite: a.quantite,
      categorie: a.categorie || "",
      tva_percent: a.tva_percent ?? 20,
      prix_ttc: a.prix_ttc ?? a.prix ?? 0,
      prix_achat: a.prix_achat ?? 0,
      code: a.code || "",
      stock_type: a.stock_type || "principal",
      seuil_alerte: a.seuil_alerte ?? "",
      perissable: !!a.perissable,
      eco_participation_ht: a.eco_participation_ht || "",
      vendu_au_poids: !!a.vendu_au_poids,
      type: a.type || "article",
      composants: a.composants || [],
      duree_minutes: a.duree_minutes ?? null,
      stock_infini: !!a.stock_infini,
      tags: a.tags || [],
    });
    clearPendingPhoto();
    setPhotoPreviewUrl(a.photo_url ? `${UPLOADS_BASE}${a.photo_url}` : null);
    setOpen(true);
  };

  const save = async () => {
    try {
      let article;
      if (editing) {
        // On n'envoie que les champs que l'utilisateur a le droit de modifier
        // (mêmes conditions que celles qui déterminent l'affichage des champs
        // dans le formulaire ci-dessous) : envoyer le formulaire entier, même
        // avec des valeurs inchangées, ferait échouer la requête côté serveur
        // dès qu'un champ non autorisé (ex. "nom") est présent, même si sa
        // valeur n'a pas bougé — c'est exactement ce qui causait le "Permission
        // refusée" pour un utilisateur n'ayant que "edit_quantity".
        const payload = {};
        if (hasPerm(user, "stock", "edit")) {
          payload.nom = form.nom;
          payload.categorie = form.categorie;
          payload.tags = form.tags;
          payload.code = form.code;
          payload.perissable = form.perissable;
          payload.vendu_au_poids = form.vendu_au_poids;
          payload.stock_infini = form.stock_infini;
        }
        if (hasPerm(user, "stock", "edit_quantity")) {
          payload.quantite = form.quantite;
        }
        if (hasPerm(user, "stock", "edit_prix_vente")) {
          payload.prix_ttc = form.prix_ttc;
          payload.tva_percent = form.tva_percent;
          payload.eco_participation_ht = form.eco_participation_ht === "" ? 0 : Number(form.eco_participation_ht);
        }
        if (hasPerm(user, "stock", "edit_prix_achat")) {
          payload.prix_achat = form.prix_achat;
        }
        if (hasPerm(user, "stock", "edit_stock_type")) {
          payload.stock_type = form.stock_type;
        }
        if (hasPerm(user, "stock", "edit")) {
          if (form.seuil_alerte === "" || form.seuil_alerte === null) {
            payload.clear_seuil_alerte = true;
          } else {
            payload.seuil_alerte = Number(form.seuil_alerte);
          }
        }
        const { data } = await api.patch(`/stock/${editing.id}`, payload);
        article = data;
      } else {
        const { data } = await api.post("/stock", {
          ...form,
          seuil_alerte: form.seuil_alerte === "" ? null : Number(form.seuil_alerte),
          eco_participation_ht: form.eco_participation_ht === "" ? 0 : Number(form.eco_participation_ht),
        });
        article = data;
      }
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await api.post(`/stock/${article.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else if (pendingBankImageId) {
        await api.post(`/stock/${article.id}/photo-from-bank`, { image_id: pendingBankImageId });
      }
      toast.success("Article enregistré");
      setOpen(false);
      load();
      loadCategories();
      loadVerification();
      loadHorsStock();
      loadLowStock();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/stock/${id}`);
      toast.success("Article supprimé");
      load();
      loadVerification();
      loadHorsStock();
      loadLowStock();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="Stock">
      <Tabs defaultValue="stock">
        <TabsList className="mb-4 flex w-full flex-nowrap justify-start overflow-x-auto h-auto">
          <TabsTrigger value="stock" data-testid="stock-tab-stock" className="gap-1.5 shrink-0">
            <Package size={14} /> Stock
          </TabsTrigger>
          <TabsTrigger value="verification" data-testid="stock-tab-verification" className="gap-1.5 shrink-0">
            <ClipboardCheck size={14} /> Vérification de stock
            {verifArticles.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold">
                {verifArticles.length}
              </span>
            )}
          </TabsTrigger>
          {hasPerm(user, "stock", "hors_stock") && (
            <TabsTrigger value="hors_stock" data-testid="stock-tab-hors-stock" className="gap-1.5 shrink-0">
              <PackageX size={14} /> Produits hors stock
              {hsArticles.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-slate-500 text-white text-[10px] font-semibold">
                  {hsArticles.length}
                </span>
              )}
            </TabsTrigger>
          )}
          {hasPerm(user, "stock", "view") && (
            <TabsTrigger value="low_stock" data-testid="stock-tab-low-stock" className="gap-1.5 shrink-0">
              <BellRing size={14} /> À réapprovisionner
              {lowStockArticles.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold">
                  {lowStockArticles.length}
                </span>
              )}
            </TabsTrigger>
          )}
          {hasPerm(user, "stock", "transformation_view") && (
            <TabsTrigger value="transformation" data-testid="stock-tab-transformation" className="gap-1.5 shrink-0">
              <Repeat2 size={14} /> Transformation
            </TabsTrigger>
          )}
          {hasPerm(user, "stock", "database") && (
            <TabsTrigger value="database" data-testid="stock-tab-database" className="gap-1.5 shrink-0">
              <Database size={14} /> Base de données
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="stock">
      {articles.some((a) => a.prix_vente_auto && Number(a.quantite) !== 0) && (
        <div
          className="flex items-start gap-2.5 rounded-xl border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 mb-4"
          data-testid="stock-auto-price-warning"
        >
          <AlertTriangle size={17} className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm">
              <span className="font-semibold">{articles.filter((a) => a.prix_vente_auto && Number(a.quantite) !== 0).length} article(s) ont un prix de vente fixé automatiquement</span>
              {" "}(calculé à partir du prix d'achat fournisseur, réglage modifiable dans Administrateurs → Bénéfice par défaut) et sont affichés en rouge ci-dessous.
              Vérifiez-les et enregistrez leur vrai prix de vente.
            </p>
            {hasPerm(user, "stock", "edit_prix_vente") && (
              <Button size="sm" variant="destructive" className="gap-1.5 mt-2" onClick={ouvrirFixerPrix} data-testid="stock-fixer-prix-button">
                <Tag size={13} /> Fixer prix
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <div className="relative w-full sm:max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="stock-search-input" placeholder="Rechercher un article, une catégorie, un ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        {hasPerm(user, "stock", "add") && (
          <Button data-testid="stock-add-button" onClick={openCreate} className="gap-2 shrink-0 w-full sm:w-auto">
            <Plus size={16} /> Ajouter un article
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <ArrowUpDown size={14} className="text-muted-foreground shrink-0" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="stock-sort-select" className="w-full sm:w-56"><SelectValue placeholder="Trier par..." /></SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Select value={stockFilter} onValueChange={setStockFilter}>
          <SelectTrigger data-testid="stock-filter-select" className="w-full sm:w-56"><SelectValue placeholder="Emplacement de stock" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous les stocks</SelectItem>
            {STOCK_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={viewMode} onValueChange={changerViewMode}>
          <SelectTrigger data-testid="stock-view-mode-select" className="w-full sm:w-56 gap-1.5">
            <LayoutGrid size={14} className="shrink-0 text-muted-foreground" />
            <SelectValue placeholder="Disposition" />
          </SelectTrigger>
          <SelectContent>
            {STOCK_VIEW_MODES.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`stock-view-mode-option-${m.value}`}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {viewMode === "grille" && (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="stock-grid">
        {filtered.map((a) => {
          const qty = Number(a.quantite ?? 0);
          const isNegative = qty < 0;
          const isOutOfStock = qty === 0;
          // Stock bas : si un seuil d'alerte est configuré sur l'article, on s'y
          // fie strictement (voir onglet "À réapprovisionner"). Sinon, on retombe
          // sur l'ancienne estimation par défaut (1 ou 2 unités restantes) pour ne
          // rien changer au comportement des articles sans seuil configuré.
          const hasSeuil = a.seuil_alerte !== null && a.seuil_alerte !== undefined;
          const isLow = !isNegative && !isOutOfStock && (hasSeuil ? qty <= Number(a.seuil_alerte) : qty <= 2);
          const cardStateClass = isNegative
            ? "border-destructive bg-destructive/10"
            : isOutOfStock
            ? "border-slate-400 bg-slate-500/10"
            : isLow
            ? "border-amber-500 bg-amber-500/10"
            : "border-border bg-card";
          const qtyTextClass = isNegative
            ? "text-destructive"
            : isOutOfStock
            ? "text-slate-500 dark:text-slate-400"
            : isLow
            ? "text-amber-600 dark:text-amber-400"
            : "";
          return (
          <div key={a.id} className={`aspect-square rounded-xl border p-3 flex flex-col group relative ${cardStateClass}`} data-testid={`stock-card-${a.id}`}>
            <div className="flex-1 rounded-lg bg-secondary overflow-hidden flex items-center justify-center mb-2 relative">
              {a.photo_url ? (
                <img src={`${UPLOADS_BASE}${a.photo_url}`} alt={a.nom} className="w-full h-full object-cover" />
              ) : (
                <CategoryIcon category={resolveCategoryForIcon(categories, a.categorie)} uploadsBase={UPLOADS_BASE} size={32} className="text-muted-foreground" />
              )}
              <span
                className={`absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-medium ${(a.stock_type || "principal") === "principal" ? "bg-primary/15 text-primary" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}
                data-testid={`stock-card-stocktype-${a.id}`}
                title="Information interne, non affichée sur le ticket"
              >
                {(a.stock_type || "principal") === "principal" ? <Warehouse size={10} /> : <Truck size={10} />}
                {stockTypeLabel(a.stock_type)}
              </span>
            </div>
            <p className="text-sm font-medium truncate" data-testid={`stock-card-name-${a.id}`}>{a.nom}</p>
            {searchActive && a.categorie && (
              <p className="text-[10px] text-muted-foreground truncate" data-testid={`stock-card-breadcrumb-${a.id}`}>
                {getCategoryBreadcrumb(categories, a.categorie)}
              </p>
            )}
            {searchActive && (a.tags || []).length > 0 && (
              <p className="text-[10px] text-primary truncate" data-testid={`stock-card-tags-${a.id}`}>
                {a.tags.join(" · ")}
              </p>
            )}
            <p className="text-xs text-muted-foreground">ID: {a.code || a.id.slice(-6)}</p>
            <p className={`text-xs font-semibold ${qtyTextClass}`} data-testid={`stock-card-qty-${a.id}`}>
              Qté: {a.quantite}
              {isTestMode && a.quantite_production !== undefined && (
                <span className="ml-1.5 font-normal text-muted-foreground normal-case tracking-normal">(prod réelle : {a.quantite_production})</span>
              )}
              {isNegative && <span className="ml-1 uppercase tracking-wide text-[9px]">Stock négatif</span>}
              {isOutOfStock && <span className="ml-1 uppercase tracking-wide text-[9px]">Produits hors stock</span>}
              {isLow && <span className="ml-1 uppercase tracking-wide text-[9px]">Stock bas</span>}
            </p>
            <p
              className={`text-xs font-semibold flex items-center gap-1 ${a.prix_vente_auto && !isOutOfStock ? "text-destructive" : "text-primary"}`}
              data-testid={`stock-card-price-${a.id}`}
              title={a.prix_vente_auto && !isOutOfStock ? "Prix de vente fixé automatiquement — à vérifier" : undefined}
            >
              {a.prix_vente_auto && !isOutOfStock && <AlertTriangle size={11} className="shrink-0" />}
              {Number(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} € TTC
            </p>
            <p className="text-[10px] text-muted-foreground" data-testid={`stock-card-price-ht-${a.id}`}>
              {Number(a.prix || 0).toFixed(2)} € H.T · TVA {a.tva_percent ?? 20}%
            </p>
            {hasPerm(user, "stock", "edit_prix_achat") && (
              Number(a.prix_achat) > 0 ? (
                <p className="text-[10px] text-muted-foreground" data-testid={`stock-card-prix-achat-${a.id}`}>
                  Achat : {Number(a.prix_achat).toFixed(2)} € H.T
                </p>
              ) : (
                <p className="flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400" data-testid={`stock-card-no-prix-achat-${a.id}`}>
                  <AlertTriangle size={11} /> Pas de prix d'achat
                </p>
              )
            )}
            {canEditAnyStockField && (
              <div className="absolute top-2 right-2 hidden group-hover:flex gap-1">
                <button data-testid={`stock-edit-${a.id}`} onClick={() => openEdit(a)} className="p-1.5 rounded-md bg-background/90 border border-border">
                  <Pencil size={13} />
                </button>
                {canTransfer && (
                  <button data-testid={`stock-transfer-${a.id}`} onClick={() => openTransfer(a)} title="Transférer vers une autre boutique" className="p-1.5 rounded-md bg-background/90 border border-border">
                    <ArrowLeftRight size={13} />
                  </button>
                )}
                {hasPerm(user, "stock", "delete") && (
                  <button data-testid={`stock-delete-${a.id}`} onClick={() => remove(a.id)} className="p-1.5 rounded-md bg-background/90 border border-border text-destructive">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>
      )}

      {viewMode === "tableau" && (
        <DenseTableView
          articles={filtered} categories={categories} isTestMode={isTestMode} user={user}
          canEditAnyStockField={canEditAnyStockField} canTransfer={canTransfer}
          onEdit={openEdit} onTransfer={openTransfer} onDelete={remove} uploadsBase={UPLOADS_BASE}
        />
      )}
      {viewMode === "liste" && (
        <CompactListView
          articles={filtered} categories={categories} isTestMode={isTestMode} user={user}
          canEditAnyStockField={canEditAnyStockField} canTransfer={canTransfer}
          onEdit={openEdit} onTransfer={openTransfer} onDelete={remove} uploadsBase={UPLOADS_BASE}
        />
      )}
      {viewMode === "groupe" && (
        <GroupedCategoryView
          articles={filtered} categories={categories} isTestMode={isTestMode} user={user}
          canEditAnyStockField={canEditAnyStockField} canTransfer={canTransfer}
          onEdit={openEdit} onTransfer={openTransfer} onDelete={remove} uploadsBase={UPLOADS_BASE}
        />
      )}
      {viewMode === "priorite" && (
        <PriorityStockView
          articles={filtered} categories={categories} isTestMode={isTestMode} user={user}
          canEditAnyStockField={canEditAnyStockField} canTransfer={canTransfer}
          onEdit={openEdit} onTransfer={openTransfer} onDelete={remove} uploadsBase={UPLOADS_BASE}
        />
      )}
        </TabsContent>

        <TabsContent value="verification">
          {user?.is_admin && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5 p-3 rounded-lg border border-border bg-secondary/40">
              <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
                <Settings2 size={14} /> Délai avant vérification :
              </div>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="verif-delai-input"
                  type="number"
                  min={1}
                  className="w-24"
                  value={verifDraft}
                  onChange={(e) => setVerifDraft(e.target.value)}
                />
                <span className="text-sm text-muted-foreground">jours</span>
                <Button data-testid="verif-delai-save" size="sm" variant="outline" onClick={saveVerifSettings} disabled={verifSaving || Number(verifDraft) === verifDelai}>
                  Enregistrer
                </Button>
              </div>
            </div>
          )}

          {!user?.is_admin && (
            <p className="text-xs text-muted-foreground mb-4">
              Délai actuel avant vérification : <span className="font-medium">{verifDelai} jours</span> (modifiable par un administrateur).
            </p>
          )}

          {verifArticles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground" data-testid="verif-empty">
              <CheckCircle2 size={32} className="mb-2 opacity-60" />
              <p className="text-sm">Aucun article à vérifier pour le moment.</p>
              <p className="text-xs mt-1">Un article apparaît ici si sa quantité n'a pas bougé depuis {verifDelai} jours.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="verif-list">
              {verifArticles.map((a) => (
                <div key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid={`verif-row-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{a.nom}</p>
                    <p className="text-xs text-muted-foreground">
                      ID: {a.code || a.id.slice(-6)} · Qté actuelle: {a.quantite} · {stockTypeLabel(a.stock_type)}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Dernier mouvement : {formatLastActivity(a.stock_last_activity_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {hasPerm(user, "stock", "edit_quantity") && (
                      <Button data-testid={`verif-ok-${a.id}`} size="sm" variant="outline" className="gap-1.5" onClick={() => verifConfirmOk(a.id)}>
                        <CheckCircle2 size={13} /> Stock bon
                      </Button>
                    )}
                    {hasPerm(user, "stock", "edit") && (
                      <Button data-testid={`verif-edit-${a.id}`} size="sm" variant="outline" className="gap-1.5" onClick={() => openEdit(a)}>
                        <Pencil size={13} /> Modifier la qté
                      </Button>
                    )}
                    {hasPerm(user, "stock", "edit") && (
                      <Button data-testid={`verif-rupture-${a.id}`} size="sm" variant="outline" className="gap-1.5 text-amber-600 border-amber-500/50 hover:bg-amber-500/10" onClick={() => verifMarkRupture(a.id)}>
                        <Ban size={13} /> Rupture permanente
                      </Button>
                    )}
                    {hasPerm(user, "stock", "delete") && (
                      <Button data-testid={`verif-delete-${a.id}`} size="sm" variant="outline" className="gap-1.5 text-destructive border-destructive/50 hover:bg-destructive/10" onClick={() => verifDelete(a.id)}>
                        <Trash2 size={13} /> Supprimer
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {hasPerm(user, "stock", "hors_stock") && (
          <TabsContent value="hors_stock">
            <p className="text-xs text-muted-foreground mb-4">
              Ces articles ont une quantité en stock à zéro : ils n'apparaissent plus dans l'onglet Stock (sauf recherche explicite) tant qu'ils ne sont pas réapprovisionnés.
            </p>
            {hsArticles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground" data-testid="hors-stock-empty">
                <CheckCircle2 size={32} className="mb-2 opacity-60" />
                <p className="text-sm">Aucun produit hors stock pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="hors-stock-list">
                {hsArticles.map((a) => (
                  <div key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-slate-400/40 bg-slate-500/5 p-3" data-testid={`hors-stock-row-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{a.nom}</p>
                      <p className="text-xs text-muted-foreground">
                        ID: {a.code || a.id.slice(-6)} · {stockTypeLabel(a.stock_type)}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Dernier mouvement : {formatLastActivity(a.stock_last_activity_at)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {canEditAnyStockField && (
                        <Button data-testid={`hors-stock-edit-${a.id}`} size="sm" variant="outline" className="gap-1.5" onClick={() => openEdit(a)}>
                          <Pencil size={13} /> Réapprovisionner / Modifier
                        </Button>
                      )}
                      {hasPerm(user, "stock", "delete") && (
                        <Button data-testid={`hors-stock-delete-${a.id}`} size="sm" variant="outline" className="gap-1.5 text-destructive border-destructive/50 hover:bg-destructive/10" onClick={() => remove(a.id)}>
                          <Trash2 size={13} /> Supprimer
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {hasPerm(user, "stock", "view") && (
          <TabsContent value="low_stock">
            <p className="text-xs text-muted-foreground mb-4">
              Articles dont le seuil d'alerte (défini dans leur fiche) est atteint ou dépassé. Un article sans seuil
              configuré n'apparaît jamais ici, même à quantité basse — définissez un seuil depuis Modifier l'article.
            </p>
            {lowStockArticles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground" data-testid="low-stock-empty">
                <CheckCircle2 size={32} className="mb-2 opacity-60" />
                <p className="text-sm">Aucun article sous son seuil d'alerte pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="low-stock-list">
                {lowStockArticles.map((a) => (
                  <div key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid={`low-stock-row-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{a.nom}</p>
                      <p className="text-xs text-muted-foreground">
                        ID: {a.code || a.id.slice(-6)} · {stockTypeLabel(a.stock_type)}
                      </p>
                      <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                        Stock actuel : {a.quantite} (seuil d'alerte : {a.seuil_alerte})
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {canEditAnyStockField && (
                        <Button data-testid={`low-stock-edit-${a.id}`} size="sm" variant="outline" className="gap-1.5" onClick={() => openEdit(a)}>
                          <Pencil size={13} /> Réapprovisionner / Modifier
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}
        {hasPerm(user, "stock", "transformation_view") && (
          <TabsContent value="transformation">
            <TransformationTab articles={articles} />
          </TabsContent>
        )}
        {hasPerm(user, "stock", "database") && (
          <TabsContent value="database">
            <DatabaseTab />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={fixerPrixOpen} onOpenChange={setFixerPrixOpen}>
        <DialogContent data-testid="fixer-prix-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Fixer le prix
              {fixerPrixListe.length > 0 && (
                <span className="block text-xs font-normal text-muted-foreground mt-1">
                  Article {fixerPrixIndex + 1} sur {fixerPrixListe.length}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {articleAFixer && (
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-base" data-testid="fixer-prix-article-nom">{articleAFixer.nom}</p>
                <p className="text-sm text-muted-foreground">
                  Prix fournisseur (H.T) : <span className="font-medium" data-testid="fixer-prix-achat">{Number(articleAFixer.prix_achat || 0).toFixed(2)} €</span>
                </p>
              </div>

              <div className="rounded-lg border border-border bg-secondary/50 px-4 py-3 text-right">
                <span className="text-2xl font-bold tabular-nums" data-testid="fixer-prix-affichage">
                  {fixerPrixValeur || "0"}
                </span>
                <span className="text-lg font-semibold text-muted-foreground ml-1">€ TTC</span>
              </div>

              <NumericKeypad
                onDigit={(d) => keypadAppuyer(d)}
                onBackspace={() => keypadAppuyer("back")}
                onDecimal={() => keypadAppuyer(".")}
                showDecimal
                testIdPrefix="fixer-prix"
                keyClassName="min-h-[56px] rounded-lg border border-border text-lg font-semibold hover:border-primary active:scale-95 transition-transform duration-100 disabled:opacity-50"
              />

              <Button className="w-full gap-2" size="lg" onClick={validerPrixFixe} disabled={fixerPrixSaving} data-testid="fixer-prix-valider">
                <Check size={16} /> {fixerPrixSaving ? "Enregistrement..." : "Valider"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="stock-form-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier l'article" : "Nouvel article"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(!editing || hasPerm(user, "stock", "edit")) && (
              <>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Nom</label>
                  <Input data-testid="stock-form-nom" placeholder="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">ID article</label>
                  <Input data-testid="stock-form-code" placeholder="Laisser vide pour génération automatique" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                </div>
                {(stockConfig.types_actifs.formule || stockConfig.types_actifs.service) && (
                <div className="flex gap-2">
                  {stockConfig.types_actifs.article && (
                    <Button
                      type="button" size="sm" variant={form.type === "article" || !form.type ? "default" : "outline"}
                      onClick={() => setForm({ ...form, type: "article" })} data-testid="stock-form-type-article" className="flex-1"
                    >
                      Article normal
                    </Button>
                  )}
                  {stockConfig.types_actifs.formule && (
                    <Button
                      type="button" size="sm" variant={form.type === "formule" ? "default" : "outline"}
                      onClick={() => setForm({ ...form, type: "formule" })} data-testid="stock-form-type-formule" className="flex-1"
                    >
                      Formule / menu
                    </Button>
                  )}
                  {stockConfig.types_actifs.service && (
                    <Button
                      type="button" size="sm" variant={form.type === "service" ? "default" : "outline"}
                      onClick={() => setForm({ ...form, type: "service" })} data-testid="stock-form-type-service" className="flex-1"
                    >
                      Service
                    </Button>
                  )}
                </div>
                )}
                {form.type === "formule" && (
                  <FormuleComposantsEditor articles={articles} composants={form.composants} onChange={(composants) => setForm({ ...form, composants })} />
                )}
                {form.type === "service" && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Durée (minutes)</label>
                    <Input
                      data-testid="stock-form-duree-minutes" type="number" min="1" placeholder="30"
                      value={form.duree_minutes ?? ""} onChange={(e) => setForm({ ...form, duree_minutes: e.target.value === "" ? null : Number(e.target.value) })}
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Sert d'indication à la caisse lors de la prise de rendez-vous (modèle "Service sur
                      rendez-vous", voir Administrateurs &gt; Boutiques).
                    </p>
                  </div>
                )}
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              {form.type === "article" && (!editing || hasPerm(user, "stock", "edit_quantity")) && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Quantité</label>
                  <Input data-testid="stock-form-quantite" type="number" step={form.vendu_au_poids ? "0.001" : "1"} placeholder="0" value={form.quantite} onChange={(e) => setForm({ ...form, quantite: Number(e.target.value) })} />
                </div>
              )}
              {(!editing || hasPerm(user, "stock", "edit_prix_vente")) && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">{form.type === "formule" ? "Prix de la formule (TTC)" : "Prix TTC (€)"}</label>
                  <Input data-testid="stock-form-prix-ttc" type="number" step="0.01" placeholder="0.00" value={form.prix_ttc} onChange={(e) => setForm({ ...form, prix_ttc: Number(e.target.value) })} />
                </div>
              )}
            </div>
            {form.type === "article" && (!editing || hasPerm(user, "stock", "edit")) && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Seuil d'alerte stock bas (optionnel)</label>
                <Input
                  data-testid="stock-form-seuil-alerte"
                  type="number"
                  min="0"
                  placeholder="Laisser vide pour ne pas suivre cet article"
                  value={form.seuil_alerte}
                  onChange={(e) => setForm({ ...form, seuil_alerte: e.target.value })}
                />
                <p className="text-[10px] text-muted-foreground">
                  Dès que la quantité descend à ce seuil ou en dessous, l'article apparaît dans l'onglet "À réapprovisionner".
                </p>
                {stockConfig.champs_visibles.perissable && (
                  <>
                    <label className="flex items-center gap-2 mt-2 cursor-pointer" htmlFor="stock-form-perissable">
                      <Checkbox
                        id="stock-form-perissable"
                        data-testid="stock-form-perissable"
                        checked={form.perissable}
                        onCheckedChange={(v) => setForm({ ...form, perissable: !!v })}
                      />
                      <span className="text-xs">Article périssable (fruits, légumes, fleurs...)</span>
                    </label>
                    <p className="text-[10px] text-muted-foreground">
                      Dispense cet article du contrôle du "prix de référence à 30 jours" sur les annonces de réduction
                      (art. L112-1-1 du code de la consommation) — exemption légale réservée aux biens menacés
                      d'altération rapide.
                    </p>
                  </>
                )}
              </div>
            )}
            {form.type === "article" && stockConfig.champs_visibles.vendu_au_poids && (!editing || hasPerm(user, "stock", "edit")) && (
              <div className="space-y-1">
                <label className="flex items-center gap-2 cursor-pointer" htmlFor="stock-form-vendu-au-poids">
                  <Checkbox
                    id="stock-form-vendu-au-poids"
                    data-testid="stock-form-vendu-au-poids"
                    checked={form.vendu_au_poids}
                    onCheckedChange={(v) => setForm({ ...form, vendu_au_poids: !!v })}
                  />
                  <span className="text-xs">Vendu au poids (boucherie, primeur...)</span>
                </label>
                <p className="text-[10px] text-muted-foreground">
                  Le prix de vente saisi ci-dessus est alors un prix AU KG — la caisse demandera le poids au
                  moment de la vente (modèle "Vente au poids", voir Administrateurs &gt; Boutiques).
                </p>
              </div>
            )}
            {stockConfig.champs_visibles.eco_participation && (!editing || hasPerm(user, "stock", "edit_prix_vente")) && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Éco-participation DEEE (€ HT, optionnel)</label>
                <Input
                  data-testid="stock-form-eco-participation"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00 — laisser vide si non concerné"
                  value={form.eco_participation_ht}
                  onChange={(e) => setForm({ ...form, eco_participation_ht: e.target.value })}
                />
                <p className="text-[10px] text-muted-foreground">
                  Pour un équipement électrique/électronique neuf uniquement (loi du 15/11/2006, filière DEEE) :
                  montant fourni par votre éco-organisme, affiché distinctement sur les tickets et factures.
                </p>
              </div>
            )}
            {form.type !== "formule" && (!editing || hasPerm(user, "stock", "edit_prix_vente")) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">TVA (%)</label>
                  <Input data-testid="stock-form-tva" type="number" step="0.1" placeholder="20" value={form.tva_percent} onChange={(e) => setForm({ ...form, tva_percent: Number(e.target.value) })} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Prix H.T (calculé)</label>
                  <div className="h-9 flex items-center px-3 rounded-md border border-border bg-secondary/50 text-sm text-muted-foreground" data-testid="stock-form-prix-ht-preview">
                    {prixHtPreview.toFixed(2)} €
                  </div>
                </div>
              </div>
            )}
            {form.type === "article" && (!editing || hasPerm(user, "stock", "edit_prix_achat")) && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Prix d'achat fournisseur (H.T)</label>
                <Input
                  data-testid="stock-form-prix-achat"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.prix_achat}
                  onChange={(e) => setForm({ ...form, prix_achat: Number(e.target.value) })}
                />
                <p className="text-[10px] text-muted-foreground">
                  Sert uniquement au calcul du bénéfice dans les Statistiques — n'apparaît jamais à la caisse ni sur un document imprimé.
                  {prixHtPreview > 0 && (
                    <span className="ml-1 font-medium">
                      Marge : {(prixHtPreview - Number(form.prix_achat || 0)).toFixed(2)} € ({(((prixHtPreview - Number(form.prix_achat || 0)) / prixHtPreview) * 100).toFixed(0)}%)
                    </span>
                  )}
                </p>
              </div>
            )}
            {(!editing || hasPerm(user, "stock", "edit")) && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Catégorie</label>
                <Popover open={categoryPickerOpen} onOpenChange={setCategoryPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={categoryPickerOpen}
                      data-testid="stock-form-categorie"
                      className="w-full justify-between font-normal"
                    >
                      <span className="flex items-center gap-2 truncate">
                        {form.categorie ? (
                          <>
                            <CategoryIcon category={categories.find((c) => c.nom === form.categorie)} uploadsBase={UPLOADS_BASE} size={14} />
                            {form.categorie}
                          </>
                        ) : (
                          <span className="text-muted-foreground">Choisir une catégorie</span>
                        )}
                      </span>
                      <ChevronsUpDown size={14} className="shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                    <Command>
                      <CommandInput placeholder="Rechercher une catégorie..." data-testid="stock-form-categorie-search" />
                      <CommandList>
                        <CommandEmpty>
                          Aucune catégorie ne correspond. Les catégories se créent depuis Administrateurs &gt; Catégories.
                        </CommandEmpty>
                        <CommandGroup>
                          {categoriesOrdonnees.map((c) => (
                            <CommandItem
                              key={c.id}
                              value={c.nom}
                              data-testid={`stock-form-categorie-option-${c.id}`}
                              onSelect={() => { setForm({ ...form, categorie: c.nom }); setCategoryPickerOpen(false); }}
                              className={c.isSub ? "pl-6" : ""}
                            >
                              <Check size={14} className={form.categorie === c.nom ? "opacity-100" : "opacity-0"} />
                              {c.isSub && <CornerDownRight size={12} className="text-muted-foreground shrink-0" />}
                              <CategoryIcon category={c.isSub ? categories.find((p) => p.id === c.parent_id) : c} uploadsBase={UPLOADS_BASE} size={14} />
                              {c.nom}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-[10px] text-muted-foreground">
                  Liste des catégories configurable uniquement par un administrateur (Administrateurs &gt; Catégories).
                </p>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Tags (optionnel)</label>
              <TagPicker value={form.tags} onChange={(tags) => setForm({ ...form, tags })} />
            </div>
            {stockConfig.champs_visibles.emplacement && (!editing || hasPerm(user, "stock", "edit_stock_type")) && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Emplacement de stock</label>
                <Select value={form.stock_type} onValueChange={(v) => setForm({ ...form, stock_type: v })}>
                  <SelectTrigger data-testid="stock-form-stocktype"><SelectValue placeholder="Stock" /></SelectTrigger>
                  <SelectContent>
                    {STOCK_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">Information interne (coût/traçabilité) — n'apparaît jamais sur le ticket.</p>
              </div>
            )}
            {stockConfig.stock_infini_active && form.type === "article" && (!editing || hasPerm(user, "stock", "edit")) && (
              <div className="space-y-1">
                <label className="flex items-center gap-2 cursor-pointer" htmlFor="stock-form-stock-infini">
                  <Checkbox
                    id="stock-form-stock-infini"
                    data-testid="stock-form-stock-infini"
                    checked={form.stock_infini}
                    onCheckedChange={(v) => setForm({ ...form, stock_infini: !!v })}
                  />
                  <span className="text-xs">Stock infini</span>
                </label>
                <p className="text-[10px] text-muted-foreground">
                  Cet article n'est jamais considéré en rupture et sa quantité n'est jamais décrémentée à la vente
                  (activable/désactivable depuis Administrateurs &gt; Config Stock).
                </p>
              </div>
            )}
            {(!editing || hasPerm(user, "stock", "edit")) && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Photo</label>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-md bg-secondary overflow-hidden flex items-center justify-center shrink-0 border border-border">
                    {photoPreviewUrl ? (
                      <img src={photoPreviewUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={18} className="text-muted-foreground" />
                    )}
                  </div>
                  <Button type="button" variant="outline" size="sm" className="gap-2" onClick={openImageBank} data-testid="stock-form-open-image-bank">
                    <ImageIcon size={14} />Image
                  </Button>
                  {photoPreviewUrl && (
                    <button type="button" onClick={clearPendingPhoto} className="text-muted-foreground hover:text-destructive" title="Retirer la photo" data-testid="stock-form-clear-photo">
                      <X size={15} />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="stock-form-save" onClick={save}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={imageBankOpen} onOpenChange={setImageBankOpen}>
        <DialogContent data-testid="image-bank-dialog" className="sm:max-w-xl">
          <DialogHeader><DialogTitle>Choisir une image</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">
            Réutilisez une image déjà envoyée pour un autre article, ou ajoutez-en une nouvelle.
          </p>
          <input ref={bankFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleNewBankFile} data-testid="image-bank-file-input" />
          <div className="flex gap-2 mb-3">
            <Button type="button" variant="outline" className="gap-2 shrink-0" onClick={() => bankFileInputRef.current?.click()} data-testid="image-bank-add-button">
              <Plus size={14} />Ajouter une image
            </Button>
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom..." value={imageBankSearch}
                onChange={(e) => setImageBankSearch(e.target.value)}
                className="pl-8" data-testid="image-bank-search"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto" data-testid="image-bank-grid">
            {imageBankLoading && <p className="text-sm text-muted-foreground col-span-full">Chargement...</p>}
            {!imageBankLoading && imageBank.length === 0 && (
              <p className="text-sm text-muted-foreground col-span-full" data-testid="image-bank-empty">
                Aucune image dans la banque pour l'instant — ajoutez-en une pour commencer.
              </p>
            )}
            {!imageBankLoading && imageBank.length > 0 && imageBank.filter((img) => (img.original_name || "").toLowerCase().includes(imageBankSearch.trim().toLowerCase())).length === 0 && (
              <p className="text-sm text-muted-foreground col-span-full" data-testid="image-bank-no-match">
                Aucune image ne correspond à cette recherche.
              </p>
            )}
            {imageBank
              .filter((img) => (img.original_name || "").toLowerCase().includes(imageBankSearch.trim().toLowerCase()))
              .map((img) => (
              <button
                key={img.id}
                type="button"
                onClick={() => selectBankImage(img)}
                className="flex flex-col gap-1 rounded-md overflow-hidden border border-border hover:border-primary transition-colors duration-150 p-1"
                title={img.original_name}
                data-testid={`image-bank-item-${img.id}`}
              >
                <div className="aspect-square rounded overflow-hidden bg-secondary">
                  <img src={`${UPLOADS_BASE}${img.url}`} alt={img.original_name} className="w-full h-full object-cover" />
                </div>
                <span className="text-[10px] text-muted-foreground truncate px-0.5" data-testid={`image-bank-name-${img.id}`}>
                  {img.original_name || "Sans nom"}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent data-testid="stock-transfer-dialog">
          <DialogHeader><DialogTitle>Transférer « {transferArticle?.nom} »</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Stock actuel dans cette boutique : <span className="font-semibold">{transferArticle?.quantite}</span>
            </p>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Boutique de destination</label>
              <Select value={transferDestId} onValueChange={setTransferDestId}>
                <SelectTrigger data-testid="stock-transfer-destination"><SelectValue placeholder="Choisir une boutique" /></SelectTrigger>
                <SelectContent>
                  {shops.filter((s) => s.id !== user?.effective_shop_id).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Quantité à transférer</label>
              <Input
                data-testid="stock-transfer-quantite"
                type="number"
                min="1"
                max={transferArticle?.quantite}
                placeholder="0"
                value={transferQty}
                onChange={(e) => setTransferQty(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="stock-transfer-submit" onClick={submitTransfer} disabled={transferring} className="gap-2">
              <ArrowLeftRight size={15} /> {transferring ? "Transfert..." : "Transférer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
